import type { EngineId } from '../constants';
import { OcrError, type OcrResult, type OcrRunContext, type StatusCallback } from '../types';
import { classicRecognize, getLoadedClassicModel, terminateClassicWorker } from './classicEngine';
import { disposeNextGen, getNextGenStatus, initNextGen, nextGenRecognize } from './nextGenEngine';
import type { DecodedSource, PrepareOptions } from '../preprocess';

export interface RecognizeInput {
  source: DecodedSource;
  prepare: PrepareOptions;
}

/**
 * Strict dispatcher. Exactly one recogniser runs per call and the returned
 * result always reports the engine that actually produced it. There is no
 * silent Next-Gen → Classic fallback anywhere in this path.
 */
export async function recognizeWith(
  engineId: EngineId,
  input: RecognizeInput,
  ctx: OcrRunContext,
  onStatus?: StatusCallback,
): Promise<OcrResult> {
  let result: OcrResult;
  if (engineId === 'nextgen') {
    result = await nextGenRecognize(input, ctx, onStatus);
  } else if (engineId === 'classic') {
    result = await classicRecognize(input, ctx, onStatus);
  } else {
    throw new OcrError('ENGINE_UNAVAILABLE', `Unknown OCR engine "${engineId}".`);
  }
  if (result.engine !== engineId) {
    throw new OcrError(
      'ENGINE_UNAVAILABLE',
      'Internal engine mismatch: the result did not come from the selected engine.',
      `requested=${engineId} actual=${result.engine}`,
    );
  }
  return result;
}

export * from './routing';
export {
  terminateClassicWorker,
  getLoadedClassicModel,
  initNextGen,
  disposeNextGen,
  getNextGenStatus,
};
